import { supabase } from '@/lib/supabase';

// dashboard_announcements (sql/dashboard/001). The table is new and not in
// the generated database types yet, so calls go through a loosely-typed
// handle; rows are validated by the mapping below.

export interface Announcement {
  id: string;
  body: string;
  createdAt: string;
  expiresAt: string | null;
  authorName: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const table = () => (supabase as any).from('dashboard_announcements');

/** True when the error means sql/dashboard/001 hasn't been run */
function isMissingTable(error: { code?: string; message?: string }): boolean {
  return (
    error.code === '42P01' ||
    error.code === 'PGRST205' ||
    /dashboard_announcements/.test(error.message ?? '')
  );
}

export type AnnouncementsResult =
  | { status: 'ok'; items: Announcement[] }
  | { status: 'missing' }
  | { status: 'error'; message: string };

/** Current (unexpired) announcements, newest first */
export async function listAnnouncements(): Promise<AnnouncementsResult> {
  const { data, error } = await table()
    .select('id, body, created_at, expires_at, author:created_by (name)')
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order('created_at', { ascending: false })
    .limit(5);
  if (error) {
    return isMissingTable(error)
      ? { status: 'missing' }
      : { status: 'error', message: error.message };
  }
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return {
    status: 'ok',
    items: rows.map(r => {
      const author = Array.isArray(r.author) ? r.author[0] : r.author;
      return {
        id: String(r.id),
        body: String(r.body ?? ''),
        createdAt: String(r.created_at),
        expiresAt: (r.expires_at as string | null) ?? null,
        authorName:
          (author as { name?: string } | null | undefined)?.name ?? null,
      };
    }),
  };
}

/** Admin+ only (RLS). expiresAt: YYYY-MM-DD, shown through that day */
export async function postAnnouncement(
  body: string,
  expiresAt: string | null
): Promise<void> {
  const { error } = await table().insert({
    body: body.trim(),
    // End of the chosen day, office time is close enough for a notice
    expires_at: expiresAt
      ? new Date(`${expiresAt}T23:59:59`).toISOString()
      : null,
  });
  if (error) throw new Error(error.message);
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const { error } = await table().delete().eq('id', id);
  if (error) throw new Error(error.message);
}
